"use client";
import React from "react";
import { Controllerable, Mouseable, Styleable } from "@/@types";
import { HoverBorderGradient } from "@/components/ui/hover-border-button";
import { cn } from "@/lib/utils";

type ButtonProps = {
  isStudioBtn?: boolean;
  disabled?: boolean;
  btnSize: "small" | "large";
  image?: string;
  icon?: JSX.Element;
  iconPosition?: "left" | "right";
  text: string;
  animationRequired: boolean;
  bgType: "solid" | "transparent";
  onClick?: () => void;
  href?: string;
  isUnderline?: boolean;
  isBlack?: boolean;
  iconSize?: number;
  showMobile?: boolean;
  ct?: string;
} & Styleable &
  Pick<Controllerable, "controller"> &
  Mouseable;

const CustomButton: React.FC<ButtonProps> = (props) => {
  const {
    className,
    disabled,
    btnSize,
    image,
    icon,
    iconPosition = "left",
    text,
    animationRequired,
    bgType,
    onClick,
    controller,
    onMouseEnter,
    onMouseLeave,
    isUnderline = false,
    isBlack = false,
    iconSize = 20,
    showMobile,
    ct,
  } = props;

  // Updated background and hover styling based on the `ct` prop
  const bgClass =
    ct === "demo"
      ? bgType === "solid"
        ? "bg-black text-white font-medium hover:bg-transparent hover:border hover:border-black hover:text-black"
        : "bg-rt-black-b1 hover:bg-white hover:border hover:border-black hover:text-black"
      : bgType === "solid"
      ? "bg-black text-white font-medium"
      : "bg-rt-black-b1 hover:bg-white/70 hover:text-black";

  const disabledClass = `${
    ct === "contact" ? "bg-black text-white" : "bg-white/30 text-rt-grey-t5"
  }  cursor-not-allowed`;
  const sizeClass =
    btnSize === "small" ? "px-[15px] py-[9px] text-sm" : "py-3 px-2 text-base";
  const btnVisibility = showMobile ? "flex" : "hidden lg:flex";

  const innerContent = (
    <>
      {image && <img src={image} alt="Button Image" className="w-8 h-8" />}
      {iconPosition !== "right" && icon && <span>{icon}</span>}
      <span className={bgType === "transparent" ? "leading-[160%]" : ""}>
        {text}
      </span>
      {iconPosition === "right" && icon && <span>{icon}</span>}
    </>
  );

  const commonProps = {
    onMouseEnter,
    onMouseLeave,
    onClick,
    type: "submit" as "submit",
    className: cn(
      `flex flex-row rounded-[30px] justify-center items-center gap-2 w-auto hover:opacity-100 whitespace-nowrap ${
        disabled ? disabledClass : bgClass
      } ${sizeClass} ${btnVisibility} ${className}`
    ),
  };

  if (disabled || bgType === "transparent" || bgType === "solid") {
    return <button {...commonProps}>{innerContent}</button>;
  }

  return (
    <button
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      type="submit"
    >
      <HoverBorderGradient
        as="div"
        className={cn(
          `flex flex-row rounded-[30px] justify-center items-center gap-2 w-full hover:opacity-100 whitespace-nowrap ${bgClass} ${sizeClass} ${btnVisibility} ${className}`
        )}
      >
        {innerContent}
      </HoverBorderGradient>
    </button>
  );
};

export default CustomButton;
